<template>
	<r-pop-menu
		class="group-pop"
		:class="{
			'group-pop-cell': theme === 'cell',
			'readonly': !workbookHasPermission('view_changeTimelineColumn'),
		}"
		namespace="timeline"
		:spaceBetweenActivatorAndContent="(theme === 'cell' ? 1 : 2)"
		:relativeAlignmentContainer="(theme === 'cell' ? 0 : 0.04)"
		:relativeAlignmentActivator="0"
		:popActivatorStyleOverride="{
			'width': (theme === 'cell' ? '100%' : ''),
			'height': (theme === 'cell' ? '100%' : ''),
		}"
		:disabled="!workbookHasPermission('view_changeTimelineColumn')"
	>
		<div 
			slot="activator" 
			class="group-label"
			:class="{
				'group-label-cell': theme === 'cell',
			}"
		>
			<span class="field-label" v-if="selectedGroupFieldGUID">
				<universal-field-label-display
					:dataStore="dataStore"
					:fieldGUID="selectedGroupFieldGUID"
				></universal-field-label-display>
			</span>
			<span 
				v-else
				class="field-label"
				:class="{
					'field-label-missing-cell': theme === 'cell',
				}"
			>
				Field Missing
			</span>
			<div class="arrow-icon-container">
				<r-standard-icon
					v-if="workbookHasPermission('view_changeTimelineColumn')"
					class="arrow-icon"
					icon="smallArrowDown"
					:size="{
						height: 6,
						width: 9,
					}"
					:hexColor="(theme === 'cell' ? '#C3C9D4' : '#2C2C38')"
				></r-standard-icon>
			</div>
		</div>
		<div slot="content" class="group-select-pop-content">
			<r-pop-list>
				<r-pop-list-item
					v-for="fieldStruct, index in availableGroupFields"
					:key="fieldStruct.fieldGUID"
					@pointerdown="groupFieldSelected(fieldStruct.fieldGUID)"
				>
					<div class="list-item">
						<div class="possible-checkmark">
							<r-standard-icon
								class="checkmark-icon"
								v-if="fieldStruct.fieldGUID === selectedGroupFieldGUID"
								icon="checkMark"
							></r-standard-icon>
						</div>
						<div class="choice-label">
							<universal-field-label-display
								:dataStore="dataStore"
								:fieldGUID="fieldStruct.fieldGUID"
							></universal-field-label-display>
						</div>
					</div>
				</r-pop-list-item>
				<r-pop-list-item
					v-if="workbookHasPermission('field_addNew')"
					@pointerdown="addNewGroupField"
				>
					<div class="list-item">
						<div class="possible-checkmark">
							<r-standard-icon
								class="plus-icon"
								icon="dropdownPlus"								
								:originalSize="true"
							></r-standard-icon>
						</div>
						<div class="add-new-label">
							New dropdown field
						</div>
					</div>
				</r-pop-list-item>
			</r-pop-list>
			<div class="pop-description">
				Any dropdown field can become a row in this Timeline view.
			</div>
			<div 
				v-if="availableGroupFields.length === 0"
				class="pop-description"
			>
				Create a dropdown field and add values to plot tasks on the timeline.
			</div>
		</div>
	</r-pop-menu>
</template>
<script>

import TimelineRowExample from 'vue/sheet-views/common/fields/svg-examples/timeline-row-example';
import UniversalFieldLabelDisplay from 'vue/sheet-views/common/fields/universal-field-label-display.vue';

export default {
	name:'timeline-group-selector',
	$pubsub:{
		newColumnModal:{},
	},
	components:{
		UniversalFieldLabelDisplay,
	},
	data(){
		return{
		};
	},
	inject:['sheetGlobalState', 'workbookHasPermission'],
	props:{
		sheetView:{
			required: true,
			type: Object,
		},
		theme:{
			required: false,
			type: String,
			default: 'header', //cell
		},
	},
	computed:{
		schema(){
			return this.sheetGlobalState.dataStore.meta.schema;
		},
		dataStore(){
			return this.sheetView.sheetViewGroup.dataStore;
		},
		selectedGroupField(){
			if(this.selectedGroupFieldGUID){
				return this.schema.fields[this.selectedGroupFieldGUID]
			}
		},
		selectedGroupFieldGUID(){
			if(!!this.schema.fields[this.sheetView.properties.fields.groupBy]){
				return this.sheetView.properties.fields.groupBy;
			}
			return null;
		},
		selectedGroupFieldLabel(){
			if(this.selectedGroupField){
				return this.selectedGroupField.display.label;
			}
		},
		dropdownFieldGUIDs(){
			return this.sheetGlobalState.dataStore.getAllPicklistFieldGUIDs(this.$root.systemState, this.sheetGlobalState.ConnectedApps);
		},
		availableGroupFields(){
			const vcSelf = this;
			return vcSelf.dropdownFieldGUIDs.map(function mapToStruct(fieldGUID){
				return {
					fieldGUID,
					field: vcSelf.schema.fields[fieldGUID],
				}
			}).sort(function compareFieldStructs(fieldStructA, fieldStructB){
				return fieldStructA.field.display.label.localeCompare(fieldStructB.field.display.label);
			});
		},
	},
	methods:{
		groupFieldSelected(fieldGUID){
			this.sheetView.timelineGroupFieldChanged(fieldGUID);
		},
		getSelectedGroupFieldGUID(){
			return this.selectedGroupFieldGUID;
		},
		addNewGroupField(){
			const vcSelf = this;
			vcSelf.$pubsub.modules.newColumnModal.pub('openModal', {
				sheetView: vcSelf.sheetView,
				overrideObject:{
					title: 'Add New Task Data to Use as Timeline Rows',
					subtitle: 'Add a new data field to each task that you can use to organize them on the timeline.',
					fieldType: 'select',
					fieldTerminology: true,
					showFeatures: false,
					example: TimelineRowExample,
					onCompletedCallback(editedSheetView, addedFieldGUID){
						vcSelf.groupFieldSelected(addedFieldGUID);
					},
				}
			});
		},
	},
}
</script>

<style scoped lang="scss">

.group-pop{
	cursor: pointer;

	&.group-pop-cell{
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
	}

	&.readonly{
		cursor: default !important;
	}


	.group-label{
		user-select: none;
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;
	    height: 100%;
	    width: 100%;
	    position: relative;

	    &.group-label-cell{
	    	padding-left: 12px;
	    	height: 100%;
	    	display: flex;
    	    align-items: center;
	    }

	    .field-label{
	    	display: inline-block;
    	    flex-grow: 1;
    	    white-space: nowrap;
    	    overflow: hidden;
    	    text-overflow: ellipsis;

    	    &.field-label-missing-cell{
    	    	color: $p-color-grey-400;
    	    }
	    }

	    .arrow-icon-container{
	    	width: 30px;
	    	height: 22px;
	    	cursor: pointer;		
	    	display: flex;
	    	align-items: center;
	    	justify-content: center;
	    	align-content: center;
	    	justify-items: center;
	    }
	}

	.group-select-pop-content{

		width: 300px;

		.list-item{
			display: grid;
			grid-template-columns: 39px 1fr;

			.possible-checkmark{

				.checkmark-icon{

				}

				.plus-icon{

				}
			}			
			.choice-label{
				text-overflow: ellipsis;
				white-space: nowrap;
				overflow: hidden;
				line-height: 26px;
			}

			.add-new-label{
				text-overflow: ellipsis;
				white-space: nowrap;
				overflow: hidden;
				line-height: 26px;
				color: $p-color-grey-400;
			}
		}
	

		.pop-description{
			font-size: 14px;
			line-height: 21px;
			/* or 150% */

			letter-spacing: 0.15px;

			color: #81848B;
			padding: 9px 18px 12px 18px;
		}
	}	
}

</style>
